// Tab ids and URL slugs for the account settings surface. These live in a dependency-free module
// because the app shell resolves `/settings/:slug` routes on every load, while the settings UI
// itself (SettingsModal — and with it the team-settings panel and the lucide-react icons used only
// by off-screen settings tabs) loads as a deferred chunk — see AppShell
// (QA bug bug-mu5qgtuh-9p61). Keep this file free of component and service imports.
export type SettingsTab = 'account' | 'billing' | 'plan' | 'team' | 'appearance' | 'notifications' | 'api' | 'mcp' | 'github' | 'legal' | 'experimental'

const SETTINGS_TABS: SettingsTab[] = ['account', 'billing', 'plan', 'team', 'appearance', 'notifications', 'api', 'mcp', 'github', 'legal', 'experimental']

// Three subjects, three tabs: what you have and what it costs (`account`), what you could have
// instead (`subscription`), and who else is in it (`team`). The legacy `billing` slug is the first of
// those under its old name.
const SETTINGS_TAB_SLUGS: Record<SettingsTab, string> = {
  account: 'account',
  billing: 'account',
  plan: 'subscription',
  team: 'team',
  appearance: 'appearance',
  notifications: 'notifications',
  api: 'api',
  mcp: 'mcp',
  github: 'github',
  legal: 'legal',
  experimental: 'experimental',
}

const SLUG_TO_TAB: Record<string, SettingsTab> = Object.fromEntries(
  (Object.entries(SETTINGS_TAB_SLUGS) as [SettingsTab, string][]).map(([tab, slug]) => [slug, tab]),
)

export function settingsTabToSlug(tab: SettingsTab): string {
  return SETTINGS_TAB_SLUGS[tab]
}

// Resolve a URL segment (or legacy `?settings=` value) to a tab. Accepts both the user-facing slug
// (`subscription`) and the raw tab id (`plan`) so old `?settings=plan` links keep working. Returns
// null for anything that isn't a real tab.
export function settingsSlugToTab(slug: string | null | undefined): SettingsTab | null {
  if (!slug) return null
  if (slug === 'subscription' || slug === 'plan') return 'plan'
  if (slug === 'team') return 'team'
  if (['account', 'billing'].includes(slug)) return 'account'
  if (slug === 'appearance') return 'appearance'
  if (slug in SLUG_TO_TAB) return SLUG_TO_TAB[slug]!
  if ((SETTINGS_TABS as string[]).includes(slug)) return slug as SettingsTab
  return null
}
